import type { FC } from "react";
import { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";

import { BookmarkIcon, PauseExerciseModal } from "components";

import type { UserTutoringAssignmentMinimumFragment } from "api/generated";
import VideoModal from "components/Modal/Video/VideoModal";
import { ClassroomAmbienceButton, ClassroomAmbienceProvider } from "features/ClassroomAmbience";
import { TutoringProgressBar } from "features/HeaderBar/TutoringProgressBar";
import PauseIcon from "images/icons/pause.svg";
import VideoIcon from "images/icons/youtube.svg";

interface AssignmentsData extends UserTutoringAssignmentMinimumFragment {
  type: "fixed" | "other";
}

interface Props {
  userAssignmentId: string | undefined;
  themeName: string;
  videoTips: Array<{ videoId: string; placeholderImage?: string | null | undefined }>;
  assignmentsData: AssignmentsData[];
  onQuit: () => void;
  hideSoundToggle?: boolean;
}

const TutoringBar: FC<Props> = ({
  userAssignmentId,
  themeName,
  videoTips,
  assignmentsData,
  onQuit,
  hideSoundToggle = false,
}) => {
  const { t } = useTranslation("tutoring");
  const [isPauseModalVisible, setPauseModalVisible] = useState(false);
  const [isVideoModalVisible, setVideoModalVisible] = useState(false);

  const handlePauseClick = () => {
    setPauseModalVisible(true);
  };

  const handleVideoClick = () => {
    setVideoModalVisible(true);
  };

  const handlePauseModalClose = () => {
    setPauseModalVisible(false);
  };

  const handleVideoModalClose = () => {
    setVideoModalVisible(false);
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <strong>{themeName}</strong>
        <div>
          <ClassroomAmbienceProvider isEnabled={!hideSoundToggle}>
            <Container className="lh-1 user-select-none px-3">
              <Row className="py-20px tutoring-bar__text">
                <Col className="text-end">
                  {videoTips.length ? (
                    <Button size={"sm"} onClick={handleVideoClick} className="btn-transparent ms-20px">
                      <VideoIcon />
                      <span className="p-2">Výuka</span>
                    </Button>
                  ) : null}

                  {!hideSoundToggle ? (
                    <span className="ms-20px">
                      <ClassroomAmbienceButton />
                    </span>
                  ) : null}

                  {userAssignmentId ? (
                    <>
                      <BookmarkIcon className="ms-20px" userAssignmentId={userAssignmentId} />
                    </>
                  ) : null}

                  <Button size={"sm"} variant={"navigation"} onClick={handlePauseClick} className="ms-20px p-0 ">
                    <PauseIcon />
                  </Button>
                </Col>
              </Row>

              {isPauseModalVisible ? (
                <PauseExerciseModal
                  onClose={handlePauseModalClose}
                  onQuit={onQuit}
                  isSoundToggleVisible={!hideSoundToggle}
                  continueButtonText={t("modal.pause.continue")}
                  quitButtonText={t("modal.pause.quit")}
                />
              ) : null}
              {isVideoModalVisible ? (
                <VideoModal onClose={handleVideoModalClose} themeName={themeName} videoTips={videoTips} />
              ) : null}
            </Container>
          </ClassroomAmbienceProvider>
        </div>
      </div>
      <TutoringProgressBar
        key={userAssignmentId}
        activeId={userAssignmentId}
        fixedAssignments={assignmentsData.filter(assignment => assignment.type === "fixed")}
        trainingAssignments={assignmentsData.filter(assignment => assignment.type === "other")}
      />
    </div>
  );
};

export default TutoringBar;
